import React, { useState, useEffect, useMemo } from "react"
import { AD_TYPE_IDS } from "../../../constants/adTypes"
import DefaultResourceSwitch from "./DefaultResourceSwitch"
import GenderSelector from "./GenderSelector"
import AgeRangeSelector from "./AgeRangeSelector"
import NotVideoDurationInput from "./NotVideoDurationInput"
import { AGE_RANGES } from "./aiTargetingConstants"
import "./AiTargetingForm.css"

function getUsedAgeRangesForGender(existingTargetingAds, gender) {
  return new Set(
    (existingTargetingAds || [])
      .filter((ad) => ad.gender === gender && ad.ageRange && ad.ageRange.length > 0)
      .flatMap((ad) => ad.ageRange)
  )
}

export default function AiTargetingForm({
  initialConfig = {},
  defaultToggleDisabled = false,
  isEditMode = false,
  existingTargetingAds = [],
  onConfigChange,
}) {
  const [isDefault, setIsDefault] = useState(initialConfig.isDefault || false)
  const isDefaultLocked = defaultToggleDisabled && !isDefault
  const [gender, setGender] = useState(initialConfig.gender || null)
  const [ageRange, setAgeRange] = useState(initialConfig.ageRange || [])
  const [notVideoDuration, setNotVideoDuration] = useState(initialConfig.notVideoDuration || 0)

  const usedAgeRangesForSelectedGender = useMemo(
    () => getUsedAgeRangesForGender(existingTargetingAds, gender),
    [existingTargetingAds, gender]
  )

  useEffect(() => {
    setAgeRange((prev) => {
      const filtered = prev.filter((v) => !usedAgeRangesForSelectedGender.has(v))

      return filtered.length === prev.length ? prev : filtered
    })
  }, [usedAgeRangesForSelectedGender])

  useEffect(() => {
    onConfigChange({
      isDefault,
      gender: isDefault ? null : gender,
      ageRange: isDefault ? [] : ageRange,
      notVideoDuration,
    })
  }, [isDefault, gender, ageRange, notVideoDuration, onConfigChange])

  const handleAgeRangeToggle = (value) => {
    if (usedAgeRangesForSelectedGender.has(value)) return
    if (ageRange.includes(value)) {
      setAgeRange(ageRange.filter((v) => v !== value))
    } else {
      setAgeRange([...ageRange, value])
    }
  }

  const handleSelectAllAges = () => {
    const selectableRanges = AGE_RANGES.filter(
      (r) => !usedAgeRangesForSelectedGender.has(r.value)
    ).map((r) => r.value)
    const allSelectableSelected =
      selectableRanges.length > 0 && selectableRanges.every((v) => ageRange.includes(v))
    if (allSelectableSelected) {
      setAgeRange(ageRange.filter((v) => !selectableRanges.includes(v)))
    } else {
      setAgeRange([...new Set([...ageRange, ...selectableRanges])])
    }
  }

  const showNotVideoDuration =
    initialConfig.adTypeId !== AD_TYPE_IDS.VIDEO && !isEditMode

  return (
    <div className="ai-targeting-form">
      <DefaultResourceSwitch
        isDefault={isDefault}
        isDefaultLocked={isDefaultLocked}
        onDefaultChange={setIsDefault}
      />

      {!isDefault && (
        <div className="ai-targeting-form__targeting-sections">
          <GenderSelector value={gender} onChange={setGender} />
          <AgeRangeSelector
            ageRanges={AGE_RANGES}
            selectedAgeRange={ageRange}
            usedAgeRangesForGender={usedAgeRangesForSelectedGender}
            onToggle={handleAgeRangeToggle}
            onSelectAll={handleSelectAllAges}
          />
        </div>
      )}

      {showNotVideoDuration && (
        <NotVideoDurationInput value={notVideoDuration} onChange={setNotVideoDuration} max={initialConfig.maxDuration} />
      )}
    </div>
  )
}
